import { WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import * as React from "react";
import * as printUtil from "./print-util";
import Component from "./Component";

interface IOversimplifiedWindow {
  document: Document;
  onafterprint?: () => void | null;
  addEventListener(
    type: string,
    listener: (e: Event) => void,
    useCapture: boolean
  ): void;
  onload(): void;
  close(): void;
  focus(): void;
  print(): void;
}


const styles: Record<string, CSSProperties> = {
  div: {
    height: "0",
    position: "fixed",
    width: "0",
  },
  divDeveloperMode: {
    height: "80em",
    width: "100%",
  },
  iframe: {
    border: "none",
    height: "0",
    position: "fixed",
    width: "0",
  },
  iframeDeveloperMode: {
    border: "2pt solid gold",
    height: "80%",
    overflowY: "auto",
    width: "100%",
  },
};

// tslint:disable-next-line:no-empty-interface
export interface IPrintProps {
  developerMode: boolean;
  printerRef: (printer: IPrinter | null) => void;
}

export interface IPrinter {
  printHtmlAsync(
    buildPrintElements: () => React.ReactNode,
    buildHeadElements?: () => React.ReactNode
  ): Promise<void>;
  // Must be a URI with the same origin…
  printUriAsync(uri: string): Promise<void>;
}

type IProps = IPrintProps & WithStyles<keyof typeof styles>;

interface IPrintState {
  buildHeadElements?: () => React.ReactNode;
  buildPrintElements?: () => React.ReactNode;
  printButtonClicked?: true;
  uri?: string;
}

export default withStyles(styles)(
  class extends Component<IProps, IPrintState> {
    public readonly state = this.buildState({});
    // Feature detection: on, e.g., Firefox for Android, window.print will be
    // undefined if printing isn’t supported. We should hide the printing UI entirely in this case.
    private printer: IPrinter | null = null;
    private printUriUsingWindowOpen = false;
    private serializer = Promise.resolve();
    private doPrintCalledPromise: Promise<void> = new Promise((resolve) =>
      resolve()
    );

    public componentDidMount() {
      if (this.props.printerRef !== undefined) {
        this.props.printerRef(this.printer);
      }
    }
    public componentDidUpdate(prevProps: IPrintProps) {
      if (prevProps.printerRef !== this.props.printerRef) {
        if (prevProps.printerRef !== undefined) {
          prevProps.printerRef(null);
        }
        if (this.props.printerRef !== undefined) {
          this.props.printerRef(this.printer);
        }
      }
    }
    public componentWillUnmount() {
      if (this.props.printerRef !== undefined) {
        this.props.printerRef(this.printer);
      }
    }
    public render() {
      const { classes, developerMode } = this.props;
      const { buildHeadElements, buildPrintElements, printButtonClicked, uri } =
        this.state;
      // When not printing stuff, must ensure that we render somethings
      // other than an iframe. Otherwise, we won't be able to leverage
      // onLoad. I think react might have away to force a rebuild of element
      // that could be considered more clean than this or something, but
      // we're just going to do this because I at least know it should
      // hopefully work. Well, I'm not *that* sure. Yeah, actually, we
      // need to get ref to this and wait for it to be not null to proceed.
      //
      // Note that if printing via uri, that is handled directly in the handler because opening a window must be a synchronous user-initiated event.
      if (
        buildPrintElements === undefined &&
        (this.printUriUsingWindowOpen || uri === undefined)
      ) {
        return this.renderDiv();
      }
      try {
        const html =
          buildPrintElements === undefined
            ? undefined
            : printUtil.buildPrintHtml(buildPrintElements, buildHeadElements);
        return (
          <div
            className={developerMode ? classes.divDeveloperMode : classes.div}
          >
            {developerMode ? (
              <>
                <button onClick={this.resolvePrinted}>Done</button>
                <button disabled={printButtonClicked} onClick={this.doPrint}>
                  Print
                </button>
              </>
            ) : undefined}
            <iframe
              className={
                developerMode ? classes.iframeDeveloperMode : classes.iframe
              }
              src={uri}
              srcDoc={html}
              onLoad={this.handleIframeLoad}
            />
          </div>
        );
      } catch (ex) {
        this.rejectPrinted(ex);
        return this.renderDiv();
      }
    }

    private doPrint: () => void = () => undefined;
    private openWindow = (
      uri: string,
      name: string,
      options?: string
    ): IOversimplifiedWindow | null => null;
    private resolvePrinted: () => void = () => undefined;
    private rejectPrinted: (ex: any) => void = () => undefined;
    private resolveBlanked: () => void = () => undefined;
    private resolveUri: (uri: string) => string = () => {
      throw new Error("resolveUri() not loaded yet.");
    };

    private readonly print = (
      buildPrintElements?: () => React.ReactNode,
      buildHeadElements?: () => React.ReactNode,
      uri?: string
    ) => {
      const resolvedUri = uri === undefined ? undefined : this.resolveUri(uri);
      if (resolvedUri !== undefined && this.printUriUsingWindowOpen) {
        // printing via <iframe/> cuts off top of page in Safari and sort of completely fails on Firefox (I think because we do not know how to wait long enough). So do the thing where you open a new window to print because that might be necessary. Must be non-async to succeed.
        const w = this.openWindow(resolvedUri, "");
        if (w === null) {
          throw new Error(
            `Error triggering print. Was asynchronicity injected anywhere recently?`
          );
        }
        return new Promise<void>((resolve, reject) => {
          w.onload = () => {
            (async () => {
              await this.handleWindowLoad(
                w,
                resolve,
                reject,
                Promise.resolve()
              );
              // Close window when done to clean things up for the user.
              w.close();
            })();
          };
        });
      }
      return (this.serializer = this.serializer.then(async () => {
        try {
          await new Promise<void>((resolve, reject) => {
            this.resolvePrinted = resolve;
            this.rejectPrinted = reject;
            this.doPrintCalledPromise = new Promise((resolveDoPrintCalled) => {
              this.doPrint = () => {
                resolveDoPrintCalled();
                this.doPrint = () => undefined;
                this.setState({
                  printButtonClicked: true,
                });
              };
            });
            // If not in developer mode, just proceed directly.
            if (!this.props.developerMode) {
              this.doPrint();
            }
            this.setState({
              buildHeadElements,
              buildPrintElements,
              uri: resolvedUri,
            });
          });
        } finally {
          await new Promise<void>((resolve) => {
            this.resolveBlanked = resolve;
            this.setState({
              buildHeadElements: undefined,
              buildPrintElements: undefined,
              printButtonClicked: undefined,
              uri: undefined,
            });
          });
          this.resolveBlanked = () => undefined;
        }
      }));
    };

    private renderDiv() {
      return <div ref={this.handleDivRef} />;
    }

    private handleIframeLoad = async (
      e: React.SyntheticEvent<
        HTMLIFrameElement & {
          contentWindow: IOversimplifiedWindow | null;
        }
      >
    ) => {
      const iframe = e.currentTarget;
      const iframeWindow = iframe.contentWindow;
      if (iframeWindow === null) {
        throw new Error("unreachable");
      }
      this.handleWindowLoad(
        iframeWindow,
        this.resolvePrinted,
        this.rejectPrinted,
        this.doPrintCalledPromise
      );
    };

    private handleWindowLoad = async (
      w: IOversimplifiedWindow,
      resolvePrinted: () => void,
      rejectPrinted: (ex: any) => void,
      printButtonClickedPromise: Promise<void>
    ) => {
      try {
        // The document loaded / we are at readyState === 'complete'. Now before we can proceed, we must wait
        // to ensure that fonts are loaded. Otherwise, the fonts might still not be loaded. So use
        // https://stackoverflow.com/q/5680013 to wait for the fonts to load first.
        //
        const afterPrintPromise = new Promise<void>(async (resolve) => {
          if (
            !(() => {
              try {
                // Feature-detect ;-). Will not be available for PDF, for example. https://stackoverflow.com/a/51735450
                if (w.onafterprint !== undefined) {
                  w.addEventListener("afterprint", () => resolve(), false);
                  return true;
                }
              } catch (ex) {
                console.warn(
                  "unable to wait for printing to finish. Waiting for print() to be called…: ",
                  ex
                );
              }
              return false;
            })()
          ) {
            // Wait for it to be assigned first.
            await Promise.resolve();
            await printCalledPromise;
            console.warn(
              "print() was called. Unable to wait for printing to finish. Waiting a sec…"
            );
            setTimeout(() => resolve(), 1000);
          }
        });

        const printCalledPromise = new Promise<void>((resolvePrintCalled) => {
          const onLoadedWithFonts = (f: () => void) => {
            const maybeIframeDocument = (() => {
              try {
                return w.document;
              } catch (ex) {
                return;
              }
            })();
            if (maybeIframeDocument) {
              // Safari will not render fonts properly even after document has fired onloaded, so
              // wait for this event too.
              const fonts = (maybeIframeDocument as any).fonts as
                | {
                    ready?: Promise<void>;
                  }
                | undefined;
              if (fonts && fonts.ready) {
                fonts.ready.then(f);
                return;
              }
            }
            setTimeout(f, 0);
          };
          onLoadedWithFonts(() => {
            (async () => {
              // In developer mode, this will wait for a button to be clicked to finish printing. Otherwise,
              // will just be a resolved promise.
              await printButtonClickedPromise;

              w.focus(); // IE fix
              w.print();
              resolvePrintCalled();
            })();
          });
        });
        await afterPrintPromise;
        resolvePrinted();
      } catch (ex) {
        rejectPrinted(ex);
      }
    };

    private handleDivRef = (
      div:
        | (HTMLDivElement & {
            ownerDocument?: {
              defaultView?: {
                print?: () => void;
              };
            };
          })
        | null
    ) => {
      if (div !== null) {
        this.resolveBlanked();

        // Probe for the window variable (to make compatible with grit-leadership-api x.x).
        const document = div.ownerDocument;
        if (!document) {
          throw new Error("div.ownerDocument isn’t truthy");
        }
        const window = document.defaultView;
        if (!window) {
          throw new Error("document.defaultView isn’t truthy");
        }
        // The thing is, print is not always defined. Not on Firefox for
        // Android at least…
        if ((window as { print?: () => void }).print) {
          const a = (
            document as {
              createElement(s: string): HTMLAnchorElement & {
                href: string;
              };
            }
          ).createElement("a");
          this.resolveUri = (uri) => {
            a.href = uri;
            return a.href;
          };
          this.openWindow = (uri: string, name: string, options?: string) =>
            (
              window as {
                open(
                  uri: string,
                  name: string,
                  options?: string
                ): IOversimplifiedWindow | null;
              }
            ).open(uri, name, options);
          const userAgentableWindow = window as {
            navigator: {
              userAgent: string;
            };
          };
          const userAgent = userAgentableWindow.navigator.userAgent;
          this.printUriUsingWindowOpen = [
            // For some reason, printing a PDF via iframe works fine on Safari but just fails on Firefox. New window sort of works.
            userAgent.match(/Firefox\//) && userAgent.match(/Gecko\//),
            // For some reason, printing a PDF via iframe has security exception with Chrome. Open a new window (and still fail, but at least the PDF should be viewable in the new window)
            userAgent.match(/Chrome\//) && userAgent.match(/like Gecko/),
          ].some((x) => !!x);
          this.printer = {
            printHtmlAsync: (buildHeadElements, buildPrintElements) =>
              this.print(buildHeadElements, buildPrintElements),
            printUriAsync: (uri) => this.print(undefined, undefined, uri),
          };
        }
      }
    };
  }
);



// WEBPACK FOOTER //
// ./src/components/Print.tsx